import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Check,
  Trash2,
  FileText,
  Upload,
  File,
  ExternalLink,
  User,
  Camera,
  Edit,
} from "lucide-react";
import { studentsApi } from "./students.api";
import type {
  StudentFormData,
  StudentContact,
  StudentDocument,
  DocType,
} from "./types";
import { toastMessage } from "@/utils/toast-message";
import { apiRequest } from "@/utils/api-request";

interface Props {
  student: { uuid: string } | null;
  onClose: () => void;
  onSaved: () => void;
}

interface ClassOption {
  id: number;
  class_label: string;
}
interface SectionOption {
  id: number;
  section_label: string;
}
interface CurriculumGroup {
  curriculum_id: number;
  curriculum_name: string;
  classes: ClassOption[];
}

const STEPS = ["Basic Info", "Academic", "Contacts", "Documents"];

const DOC_TYPES: { value: DocType; label: string }[] = [
  { value: "other", label: "Other" },
  { value: "report_card", label: "Report Card" },
  { value: "circular", label: "Circular" },
  { value: "timetable", label: "Timetable" },
  { value: "homework", label: "Homework" },
  { value: "assignment", label: "Assignment" },
  { value: "newsletter", label: "Newsletter" },
];

const EMPTY_FORM: StudentFormData = {
  first_name: "",
  last_name: "",
  admission_number: "",
  roll_number: "",
  date_of_birth: "",
  gender: "",
  blood_group: "",
  category: "",
  religion: "",
  nationality: "Indian",
  joining_date: "",
  class_id: null,
  section_id: null,
  billed_amount: "",
};

function formatBytes(kb: number | null) {
  if (!kb) return "";
  if (kb < 1024) return `${kb} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

const StudentForm: React.FC<Props> = ({ student, onClose, onSaved }) => {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [studentUuid, setStudentUuid] = useState<string | null>(
    student?.uuid ?? null,
  );
  const [form, setForm] = useState<StudentFormData>(EMPTY_FORM);
  const [contacts, setContacts] = useState<Partial<StudentContact>[]>([]);
  const [newContact, setNewContact] = useState<Partial<StudentContact>>({
    relation_type: "father",
    full_name: "",
  });
  const [addingContact, setAddingContact] = useState(false);
  const [classGroups, setClassGroups] = useState<CurriculumGroup[]>([]);
  const [sections, setSections] = useState<SectionOption[]>([]);
  const [documents, setDocuments] = useState<StudentDocument[]>([]);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadDocType, setUploadDocType] = useState<DocType>("other");
  const [uploading, setUploading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [editingContactId, setEditingContactId] = useState<number | null>(null);
  const [contactDraft, setContactDraft] = useState<Partial<StudentContact>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  // Portal CSS field styling
  const fieldCls = "field";

  useEffect(() => {
    apiRequest<any>("/api/parent-app/admin/classes", { method: "GET" }).then(
      (res) => {
        if (res.success) setClassGroups(res.data?.grouped ?? []);
      },
    );
  }, []);

  useEffect(() => {
    if (!student?.uuid) return;
    studentsApi.get(student.uuid).then((res) => {
      if (!res.success || !res.data) {
        toastMessage(
          "Failed to load student details. Please close and try again.",
          "error",
        );
        return;
      }
      const s = res.data;
      setForm({
        first_name: s.first_name ?? "",
        last_name: s.last_name ?? "",
        admission_number: s.admission_number ?? "",
        roll_number: s.roll_number ?? "",
        date_of_birth: s.date_of_birth ?? "",
        gender: s.gender ?? "",
        blood_group: s.blood_group ?? "",
        category: s.category ?? "",
        religion: s.religion ?? "",
        nationality: s.nationality ?? "Indian",
        joining_date: s.joining_date ?? "",
        class_id: s.class_id,
        section_id: s.section_id,
        billed_amount: s.billed_amount != null ? String(s.billed_amount) : "",
      });
      setContacts(s.contacts ?? []);
      setDocuments(s.documents ?? []);
      setPhotoUrl(s.photo_url ?? null);
      if (s.class_id) {
        apiRequest<any>(
          `/api/parent-app/admin/classes/${s.class_id}/sections`,
          { method: "GET" },
        ).then((r) => {
          if (r.success) setSections(r.data?.data ?? []);
        });
      }
    });
  }, [student?.uuid]);

  const nameRegex = /^[a-zA-Z\s\-'.]+$/;

  const saveBasic = async () => {
    if (!form.first_name.trim()) { toastMessage("First name is required", "error"); return; }
    if (!nameRegex.test(form.first_name.trim())) { toastMessage("First name should contain only letters", "error"); return; }
    if (!form.last_name?.trim()) { toastMessage("Last name is required", "error"); return; }
    if (!nameRegex.test(form.last_name.trim())) { toastMessage("Last name should contain only letters", "error"); return; }
    if (!form.date_of_birth) { toastMessage("Date of birth is required", "error"); return; }
    if (!form.gender) { toastMessage("Gender is required", "error"); return; }

    setSaving(true);
    try {
      if (!studentUuid) {
        const res = await studentsApi.create(form);
        if (res.success && res.data) {
          setStudentUuid(res.data.uuid);
          setPhotoUrl(res.data.photo_url ?? null);
          setStep(1);
        } else toastMessage(res.message || "Failed to create student", "error");
      } else {
        const res = await studentsApi.update(studentUuid, form);
        if (res.success) setStep(1);
        else toastMessage(res.message || "Failed to save", "error");
      }
    } finally {
      setSaving(false);
    }
  };

  const saveAcademic = async () => {
    if (!studentUuid) return;
    if (!form.class_id) {
      toastMessage("Class is required", "error");
      return;
    }
    if (!form.billed_amount || parseFloat(form.billed_amount) <= 0) {
      toastMessage("Annual fee (billed amount) is required", "error");
      return;
    }
    setSaving(true);
    try {
      const res = await studentsApi.update(studentUuid, {
        class_id: form.class_id,
        section_id: form.section_id,
        billed_amount: form.billed_amount,
      });
      if (res.success) setStep(2);
      else toastMessage(res.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleClassChange = (classId: number | null) => {
    setForm((f) => ({ ...f, class_id: classId, section_id: null }));
    setSections([]);
    if (classId) {
      apiRequest<any>(`/api/parent-app/admin/classes/${classId}/sections`, {
        method: "GET",
      }).then((r) => {
        if (r.success) setSections(r.data?.data ?? []);
      });
    }
  };

  const addContact = async () => {
    if (!studentUuid || !newContact.full_name || !newContact.relation_type) return;
    if (!newContact.phone_primary?.trim()) { toastMessage('Phone number is required', 'error'); return; }
    setAddingContact(true);
    try {
      const res = await studentsApi.createContact(studentUuid, newContact);
      if (res.success && res.data) {
        setContacts((prev) => [...prev, res.data as Partial<StudentContact>]);
        setNewContact({ relation_type: "father", full_name: "" });
        toastMessage("Contact added", "success");
      } else toastMessage(res.message || "Failed to add contact", "error");
    } finally {
      setAddingContact(false);
    }
  };

  const makePrimary = async (contactId: number) => {
    if (!studentUuid) return;
    const res = await studentsApi.updateContact(studentUuid, contactId, {
      is_primary_guardian: true,
    });
    if (res.success) {
      setContacts((prev) =>
        prev.map((c) => ({
          ...c,
          is_primary_guardian: (c as any).id === contactId,
        })),
      );
      toastMessage("Primary guardian updated", "success");
    } else toastMessage(res.message || "Failed to update", "error");
  };

  const saveContact = async (contactId: number) => {
    if (!studentUuid) return;
    if (!contactDraft.full_name?.trim()) { toastMessage('Full name is required', 'error'); return; }
    if (!contactDraft.phone_primary?.trim()) { toastMessage('Phone number is required', 'error'); return; }
    setSaving(true);
    try {
      const res = await studentsApi.updateContact(studentUuid, contactId, {
        ...contactDraft,
        full_name: contactDraft.full_name.trim(),
      } as Partial<StudentContact>);
      if (res.success && res.data) {
        setContacts((prev) =>
          prev.map((c) =>
            (c as any).id === contactId
              ? (res.data as Partial<StudentContact>)
              : c,
          ),
        );
        setEditingContactId(null);
        setContactDraft({});
        toastMessage("Contact updated", "success");
      } else {
        toastMessage(res.message || "Failed to update contact", "error");
      }
    } finally {
      setSaving(false);
    }
  };

  const deleteContact = async (contactId: number) => {
    if (!studentUuid) return;
    const contact = contacts.find((c) => (c as any).id === contactId);
    if ((contact as any)?.is_primary_guardian) {
      toastMessage("Cannot remove primary guardian. Set another contact as primary first.", "error");
      return;
    }
    const res = await studentsApi.deleteContact(studentUuid, contactId);
    if (res.success) {
      setContacts((prev) => prev.filter((c) => (c as any).id !== contactId));
      toastMessage("Contact removed", "success");
    } else toastMessage(res.message || "Failed to remove contact", "error");
  };

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !studentUuid) return;
    if (!file.type.startsWith("image/")) {
      toastMessage("Please select an image file", "error");
      return;
    }
    setUploadingPhoto(true);
    try {
      const res = await studentsApi.uploadPhoto(studentUuid, file);
      if (res.success && res.data?.photo_url) {
        setPhotoUrl(res.data.photo_url);
        toastMessage("Photo updated", "success");
      } else toastMessage(res.message || "Upload failed", "error");
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadFile(file);
    if (!uploadTitle) setUploadTitle(file.name.replace(/\.[^.]+$/, ""));
  };

  const uploadDocument = async () => {
    if (!studentUuid || !uploadFile || !uploadTitle.trim()) return;
    setUploading(true);
    try {
      const res = await studentsApi.uploadDocument(
        studentUuid,
        uploadFile,
        uploadTitle.trim(),
        uploadDocType,
      );
      if (res.success && res.data) {
        setDocuments((prev) => [res.data!, ...prev]);
        setUploadFile(null);
        setUploadTitle("");
        setUploadDocType("other");
        if (fileInputRef.current) fileInputRef.current.value = "";
        toastMessage("Document uploaded", "success");
      } else toastMessage(res.message || "Upload failed", "error");
    } finally {
      setUploading(false);
    }
  };

  const deleteDocument = async (docId: number) => {
    if (!studentUuid) return;
    const res = await studentsApi.deleteDocument(studentUuid, docId);
    if (res.success) {
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
      toastMessage("Document removed", "success");
    } else toastMessage(res.message || "Failed to remove", "error");
  };

  // Portal CSS classes - no inline Tailwind
  // inp and lbl are only used for form field styling

  const handleNext = () => {
    if (step === 0) saveBasic();
    else if (step === 1) saveAcademic();
    else if (step === 2) {
      if (contacts.length === 0) { toastMessage('Please add at least one contact before proceeding', 'error'); return; }
      if (!contacts.some((c) => c.is_primary_guardian)) { toastMessage('Please mark one contact as primary guardian before proceeding', 'error'); return; }
      setStep(3);
    }
    else if (step === 3) {
      toastMessage("Student saved", "success");
      onSaved();
    }
  };

  const handleSaveOnly = () => {
    if (step === 0) saveBasicOnly();
    else if (step === 1) saveAcademicOnly();
  };

  const saveBasicOnly = async () => {
    if (!form.first_name?.trim()) { toastMessage("First name is required", "error"); return; }
    if (!nameRegex.test(form.first_name.trim())) { toastMessage("First name should contain only letters", "error"); return; }
    if (!form.last_name?.trim()) { toastMessage("Last name is required", "error"); return; }
    if (!nameRegex.test(form.last_name.trim())) { toastMessage("Last name should contain only letters", "error"); return; }
    if (!form.date_of_birth) { toastMessage("Date of birth is required", "error"); return; }
    if (!form.gender) { toastMessage("Gender is required", "error"); return; }

    setSaving(true);
    try {
      if (!studentUuid) {
        const res = await studentsApi.create(form);
        if (res.success && res.data) {
          setStudentUuid(res.data.uuid);
          setPhotoUrl(res.data.photo_url ?? null);
          toastMessage("Student saved", "success");
        } else toastMessage(res.message || "Failed to save", "error");
      } else {
        const res = await studentsApi.update(studentUuid, form);
        if (res.success) toastMessage("Saved", "success");
        else toastMessage(res.message || "Failed to save", "error");
      }
    } finally {
      setSaving(false);
    }
  };

  const saveAcademicOnly = async () => {
    if (!studentUuid) return;
    if (!form.class_id) {
      toastMessage("Class is required", "error");
      return;
    }
    if (!form.billed_amount || parseFloat(form.billed_amount) <= 0) {
      toastMessage("Annual fee (billed amount) is required", "error");
      return;
    }
    setSaving(true);
    try {
      const res = await studentsApi.update(studentUuid, {
        class_id: form.class_id,
        section_id: form.section_id,
        billed_amount: form.billed_amount,
      });
      if (res.success) toastMessage("Saved", "success");
      else toastMessage(res.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  const canJumpToStep = () => !!studentUuid;

  return (
    <div
      className="scrim"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 100 }}
    >
      <div className="drawer wide" onClick={(e) => e.stopPropagation()}>
        {/* Hidden photo input */}
        <input
          ref={photoInputRef}
          type="file"
          style={{ display: "none" }}
          accept="image/*"
          onChange={handlePhotoSelect}
        />

        {/* Drawer Head */}
        <div className="drawer-head">
          <button
            onClick={() => studentUuid && photoInputRef.current?.click()}
            disabled={!studentUuid || uploadingPhoto}
            style={{
              position: "relative",
              width: 56,
              height: 56,
              borderRadius: "50%",
              overflow: "visible",
              background: "var(--surface-tint)",
              border: "2px solid var(--border)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: studentUuid ? "pointer" : "not-allowed",
              opacity: uploadingPhoto ? 0.6 : 1,
              flexShrink: 0,
            }}
            title={
              studentUuid
                ? "Change photo"
                : "Save Basic Info first to upload photo"
            }
          >
            {uploadingPhoto ? (
              <Loader2
                size={20}
                style={{ animation: "spin 1s linear infinite" }}
              />
            ) : photoUrl ? (
              <img
                src={photoUrl}
                alt="Student photo"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--text-3)",
                }}
              >
                <User size={24} />
                <div
                  style={{
                    position: "absolute",
                    right: -5,
                    bottom: -5,
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: "#000",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    zIndex: 2,
                  }}
                >
                  <Camera size={12} />
                </div>
              </div>
            )}
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 800,
                letterSpacing: "-0.4px",
              }}
            >
              {student ? "Edit Student" : "Add Student"}
            </h2>
            <p
              style={{
                margin: "2px 0 0",
                fontSize: 13,
                color: "var(--text-2)",
                fontWeight: 500,
              }}
            >
              {studentUuid
                ? photoUrl
                  ? "Click photo to change"
                  : "Hover photo to upload"
                : "Photo can be added after saving"}
            </p>
          </div>
          <button onClick={onClose} className="icon-btn">
            <X size={20} />
          </button>
        </div>

        {/* Step indicator */}
        <div
          style={{
            padding: "14px 20px",
            borderBottom: "1px solid var(--divider)",
            overflowX: "auto",
            flexShrink: 0,
          }}
        >
          <div className="steps" style={{ whiteSpace: "nowrap" }}>
            {STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <div
                  className={`step ${i < step ? "done" : i === step ? "active" : ""}`}
                  style={{ cursor: canJumpToStep() ? "pointer" : "default" }}
                  onClick={() => canJumpToStep() && setStep(i)}
                >
                  <div className="step-dot">
                    {i < step ? <Check size={14} /> : i + 1}
                  </div>
                  <span className="step-label">{s}</span>
                </div>
                {i < STEPS.length - 1 && <div className="step-line" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Drawer Body */}
        <div className="drawer-body">
          {/* Step 0: Basic Info */}
          {step === 0 && (
            <div className="col gap-16">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 14,
                }}
              >
                <div className={fieldCls}>
                  <label>
                    First Name <span className="req">*</span>
                  </label>
                  <input
                    value={form.first_name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, first_name: e.target.value }))
                    }
                    className="input"
                    placeholder="Enter first name"
                  />
                </div>
                <div className={fieldCls}>
                  <label>Last Name <span className="req">*</span></label>
                  <input
                    value={form.last_name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, last_name: e.target.value }))
                    }
                    className="input"
                    placeholder="Enter last name"
                  />
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 14,
                }}
              >
                <div className={fieldCls}>
                  <label>Date of Birth <span className="req">*</span></label>
                  <input
                    type="date"
                    value={form.date_of_birth}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, date_of_birth: e.target.value }))
                    }
                    className="input"
                  />
                </div>
                <div className={fieldCls}>
                  <label>Gender <span className="req">*</span></label>
                  <select
                    value={form.gender}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, gender: e.target.value }))
                    }
                    className="select"
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 14,
                }}
              >
                <div className={fieldCls}>
                  <label>Admission Number</label>
                  <input
                    value={form.admission_number}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        admission_number: e.target.value,
                      }))
                    }
                    className="input"
                    placeholder="e.g. ADM-2024-001"
                  />
                </div>
                <div className={fieldCls}>
                  <label>Roll Number</label>
                  <input
                    value={form.roll_number}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, roll_number: e.target.value }))
                    }
                    className="input"
                    placeholder="e.g. 42"
                  />
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 14,
                }}
              >
                <div className={fieldCls}>
                  <label>Blood Group</label>
                  <select
                    value={form.blood_group}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, blood_group: e.target.value }))
                    }
                    className="select"
                  >
                    <option value="">Select</option>
                    {["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"].map(
                      (g) => (
                        <option key={g} value={g}>
                          {g}
                        </option>
                      ),
                    )}
                  </select>
                </div>
                <div className={fieldCls}>
                  <label>Category</label>
                  <select
                    value={form.category}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, category: e.target.value }))
                    }
                    className="select"
                  >
                    <option value="">Select</option>
                    {[
                      "general",
                      "sc",
                      "st",
                      "obc",
                      "obc_a",
                      "obc_b",
                      "ews",
                    ].map((c) => (
                      <option key={c} value={c}>
                        {c.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 14,
                }}
              >
                <div className={fieldCls}>
                  <label>Religion</label>
                  <select
                    value={form.religion}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, religion: e.target.value }))
                    }
                    className="select"
                  >
                    <option value="">Select</option>
                    {[
                      "Hindu",
                      "Muslim",
                      "Christian",
                      "Sikh",
                      "Jain",
                      "Buddhist",
                      "Parsi",
                      "Jewish",
                      "Other",
                    ].map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={fieldCls}>
                  <label>Nationality</label>
                  <input
                    value={form.nationality}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, nationality: e.target.value }))
                    }
                    className="input"
                  />
                </div>
              </div>
              <div className={fieldCls}>
                <label>Joining Date</label>
                <input
                  type="date"
                  value={form.joining_date}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, joining_date: e.target.value }))
                  }
                  className="input"
                />
              </div>
            </div>
          )}

          {/* Step 1: Academic */}
          {step === 1 && (
            <div className="col gap-16">
              <div className={fieldCls}>
                <label>Class</label>
                <select
                  value={form.class_id ?? ""}
                  onChange={(e) =>
                    handleClassChange(
                      e.target.value ? parseInt(e.target.value) : null,
                    )
                  }
                  className="select"
                >
                  <option value="">Select class</option>
                  {classGroups.map((g) => (
                    <optgroup key={g.curriculum_id} label={g.curriculum_name}>
                      {g.classes.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.class_label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              {sections.length > 0 && (
                <div className={fieldCls}>
                  <label>Section</label>
                  <select
                    value={form.section_id ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        section_id: e.target.value
                          ? parseInt(e.target.value)
                          : null,
                      }))
                    }
                    className="select"
                  >
                    <option value="">No section</option>
                    {sections.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.section_label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {classGroups.length === 0 && (
                <p
                  className="muted"
                  style={{
                    textAlign: "center",
                    padding: "16px 0",
                    fontSize: 13.5,
                  }}
                >
                  No classes configured yet. You can assign a class later from
                  student settings.
                </p>
              )}
              <div className={fieldCls}>
                <label>Annual Fee / Billed Amount (₹) <span style={{ color: 'var(--danger)' }}>*</span></label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={form.billed_amount}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, billed_amount: e.target.value }))
                  }
                  className="input"
                  placeholder="e.g. 50000"
                />
              </div>
            </div>
          )}

          {/* Step 2: Contacts + Invite */}
          {step === 2 && studentUuid && (
            <div className="col gap-14">
              <p
                style={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: "var(--text)",
                }}
              >
                Parent / Guardian Contacts
              </p>
              {contacts.length === 0 && (
                <p
                  className="muted"
                  style={{
                    fontSize: 13,
                    textAlign: "center",
                    padding: "8px 0",
                  }}
                >
                  No contacts added yet.
                </p>
              )}
              {contacts.map((c, i) => {
                const cId = (c as any).id as number;
                const isEditing = editingContactId === cId;
                return (
                  <div key={i} className="card card-pad col gap-12">
                    {isEditing ? (
                      <div className="col gap-12">
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 12,
                          }}
                        >
                          <select
                            value={
                              contactDraft.relation_type ??
                              c.relation_type ??
                              "father"
                            }
                            onChange={(e) =>
                              setContactDraft((d) => ({
                                ...d,
                                relation_type: e.target.value as any,
                              }))
                            }
                            className="select"
                          >
                            {[
                              "father",
                              "mother",
                              "guardian",
                              "grandparent",
                              "uncle",
                              "aunt",
                              "emergency",
                              "other",
                            ].map((r) => (
                              <option key={r} value={r}>
                                {r.charAt(0).toUpperCase() + r.slice(1)}
                              </option>
                            ))}
                          </select>
                          <input
                            placeholder="Full name"
                            value={contactDraft.full_name ?? c.full_name ?? ""}
                            onChange={(e) =>
                              setContactDraft((d) => ({
                                ...d,
                                full_name: e.target.value,
                              }))
                            }
                            className="input"
                          />
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 12,
                          }}
                        >
                          <input
                            placeholder="Phone number"
                            value={
                              contactDraft.phone_primary ??
                              c.phone_primary ??
                              ""
                            }
                            onChange={(e) =>
                              setContactDraft((d) => ({
                                ...d,
                                phone_primary: e.target.value,
                              }))
                            }
                            className="input"
                          />
                          <input
                            placeholder="Email"
                            value={contactDraft.email ?? c.email ?? ""}
                            onChange={(e) =>
                              setContactDraft((d) => ({
                                ...d,
                                email: e.target.value,
                              }))
                            }
                            className="input"
                          />
                        </div>
                        <div
                          style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
                        >
                          <button
                            onClick={() => saveContact(cId)}
                            className="btn btn-primary btn-sm"
                            disabled={saving}
                          >
                            {saving ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            onClick={() => {
                              setEditingContactId(null);
                              setContactDraft({});
                            }}
                            className="btn btn-soft btn-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div
                          className="row"
                          style={{ justifyContent: "space-between", gap: 12 }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              className="row gap-8"
                              style={{ flexWrap: "wrap", marginBottom: 6 }}
                            >
                              <span
                                style={{
                                  fontSize: 13.5,
                                  fontWeight: 700,
                                  color: "var(--text)",
                                }}
                              >
                                {c.full_name}
                              </span>
                              <span
                                className="meta"
                                style={{
                                  fontSize: 11,
                                  textTransform: "capitalize",
                                }}
                              >
                                {c.relation_type}
                              </span>
                              {c.is_primary_guardian && (
                                <span className="badge b-info">Primary</span>
                              )}
                              {c.invite_status === "accepted" && (
                                <span className="badge b-success">
                                  App Active
                                </span>
                              )}
                            </div>
                            {c.phone_primary && (
                              <div className="meta" style={{ marginBottom: 2 }}>
                                {c.phone_primary}
                              </div>
                            )}
                            {c.email && <div className="meta">{c.email}</div>}
                          </div>
                          <div className="row gap-8">
                            {!c.is_primary_guardian && (
                              <button
                                onClick={() => makePrimary(cId)}
                                className="btn btn-soft btn-sm"
                                title="Set as primary guardian"
                              >
                                Make Primary
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setEditingContactId(cId);
                                setContactDraft({ ...c });
                              }}
                              className="icon-btn"
                              title="Edit contact"
                            >
                              <Edit size={16} />
                            </button>
                            {c.invite_status !== "accepted" && (
                              <button
                                onClick={() => deleteContact(cId)}
                                className="icon-btn"
                                style={{ color: "var(--danger)" }}
                                title="Remove contact"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}

              {/* Add contact form */}
              <div
                className="add-card"
                style={{
                  minHeight: 56,
                  flexDirection: "column",
                  gap: 12,
                  padding: 16,
                }}
              >
                <p
                  className="meta"
                  style={{
                    fontSize: 11,
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                    fontWeight: 800,
                  }}
                >
                  Add Contact
                </p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 12,
                  }}
                >
                  <select
                    value={newContact.relation_type}
                    onChange={(e) =>
                      setNewContact((c) => ({
                        ...c,
                        relation_type: e.target.value as any,
                      }))
                    }
                    className="select"
                  >
                    {[
                      "father",
                      "mother",
                      "guardian",
                      "grandparent",
                      "uncle",
                      "aunt",
                      "emergency",
                      "other",
                    ].map((r) => (
                      <option key={r} value={r}>
                        {r.charAt(0).toUpperCase() + r.slice(1)}
                      </option>
                    ))}
                  </select>
                  <input
                    placeholder="Full name *"
                    value={newContact.full_name ?? ""}
                    onChange={(e) =>
                      setNewContact((c) => ({
                        ...c,
                        full_name: e.target.value,
                      }))
                    }
                    className="input"
                  />
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 12,
                  }}
                >
                  <input
                    placeholder="Phone number *"
                    value={newContact.phone_primary ?? ""}
                    onChange={(e) =>
                      setNewContact((c) => ({
                        ...c,
                        phone_primary: e.target.value,
                      }))
                    }
                    className="input"
                  />
                  <input
                    placeholder="Email"
                    value={newContact.email ?? ""}
                    onChange={(e) =>
                      setNewContact((c) => ({ ...c, email: e.target.value }))
                    }
                    className="input"
                  />
                </div>
                <div
                  className="row"
                  style={{
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                  }}
                >
                  <div className="row gap-6">
                    {!contacts.some((c) => c.is_primary_guardian) && (
                      <>
                        <input
                          type="checkbox"
                          id="isPrimary"
                          checked={!!newContact.is_primary_guardian}
                          onChange={(e) =>
                            setNewContact((c) => ({
                              ...c,
                              is_primary_guardian: e.target.checked,
                            }))
                          }
                          style={{ width: 16, height: 16, cursor: "pointer" }}
                        />
                        <label htmlFor="isPrimary" className="meta">
                          Primary guardian
                        </label>
                      </>
                    )}
                  </div>
                  <button
                    onClick={addContact}
                    disabled={addingContact || !newContact.full_name || !newContact.phone_primary}
                    className="btn btn-accent btn-sm"
                    style={{
                      opacity: addingContact || !newContact.full_name || !newContact.phone_primary ? 0.5 : 1,
                    }}
                  >
                    {addingContact ? "Adding…" : "Add"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Documents */}
          {step === 3 && studentUuid && (
            <div className="col gap-16">
              <p
                style={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: "var(--text)",
                }}
              >
                Student Documents
              </p>

              {/* Upload panel */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 120,
                  padding: 16,
                  gap: 12,
                  marginBottom: 24,
                }}
              >
                <p
                  className="meta"
                  style={{
                    fontSize: 11,
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                    fontWeight: 800,
                    margin: 0,
                  }}
                >
                  Upload Document
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.xls,.xlsx,.ppt,.pptx"
                />
                {!uploadFile ? (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 8,
                      padding: 32,
                      border: "2px dashed var(--border-2)",
                      borderRadius: "var(--r-card)",
                      background: "transparent",
                      cursor: "pointer",
                      transition: "all .14s",
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.borderColor =
                        "var(--accent-deep)";
                      (e.currentTarget as HTMLElement).style.background =
                        "var(--accent-wash)";
                      (e.currentTarget as HTMLElement).style.color =
                        "var(--accent-deep)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.borderColor =
                        "var(--border-2)";
                      (e.currentTarget as HTMLElement).style.background =
                        "transparent";
                      (e.currentTarget as HTMLElement).style.color =
                        "var(--text-3)";
                    }}
                  >
                    <Upload size={32} />
                    <span
                      style={{
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: "inherit",
                      }}
                    >
                      Click to select a file
                    </span>
                    <span style={{ fontSize: 12, color: "var(--text-3)" }}>
                      PDF, Word, Excel, Image — up to 20 MB
                    </span>
                  </button>
                ) : (
                  <div className="col gap-12">
                    <div
                      className="row gap-12"
                      style={{
                        padding: 12,
                        background: "var(--surface-tint)",
                        borderRadius: "var(--r-input)",
                      }}
                    >
                      <File
                        size={32}
                        color="var(--slate)"
                        style={{ flexShrink: 0 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p
                          style={{
                            fontSize: 13.5,
                            fontWeight: 600,
                            color: "var(--text)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {uploadFile.name}
                        </p>
                        <p className="meta">
                          {(uploadFile.size / 1024).toFixed(0)} KB
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setUploadFile(null);
                          setUploadTitle("");
                          if (fileInputRef.current)
                            fileInputRef.current.value = "";
                        }}
                        className="icon-btn"
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <div className={fieldCls}>
                      <label>
                        Title <span className="req">*</span>
                      </label>
                      <input
                        value={uploadTitle}
                        onChange={(e) => setUploadTitle(e.target.value)}
                        className="input"
                        placeholder="e.g. Term 1 Report Card"
                      />
                    </div>
                    <div className={fieldCls}>
                      <label>Document Type</label>
                      <select
                        value={uploadDocType}
                        onChange={(e) =>
                          setUploadDocType(e.target.value as DocType)
                        }
                        className="select"
                      >
                        {DOC_TYPES.map((d) => (
                          <option key={d.value} value={d.value}>
                            {d.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      onClick={uploadDocument}
                      disabled={uploading || !uploadTitle.trim()}
                      className="btn btn-primary"
                      style={{
                        opacity: uploading || !uploadTitle.trim() ? 0.5 : 1,
                      }}
                    >
                      {uploading ? (
                        <Loader2
                          size={16}
                          style={{ animation: "spin 1s linear infinite" }}
                        />
                      ) : (
                        <Upload size={16} />
                      )}
                      {uploading ? "Uploading…" : "Upload"}
                    </button>
                  </div>
                )}
              </div>

              {/* Documents list */}
              {documents.length === 0 && !uploadFile && (
                <p
                  className="muted"
                  style={{
                    textAlign: "center",
                    padding: "16px 0",
                    fontSize: 13.5,
                  }}
                >
                  No documents yet.
                </p>
              )}
              {documents.map((doc) => (
                <div key={doc.id} className="card card-pad row gap-12">
                  <FileText
                    size={32}
                    color="var(--text-3)"
                    style={{ flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p
                      style={{
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: "var(--text)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        margin: 0,
                      }}
                    >
                      {doc.title}
                    </p>
                    <p className="meta">
                      {DOC_TYPES.find((d) => d.value === doc.doc_type)?.label ??
                        doc.doc_type}
                      {doc.file_size_kb
                        ? ` · ${formatBytes(doc.file_size_kb)}`
                        : ""}
                    </p>
                  </div>
                  <a
                    href={doc.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="icon-btn"
                    title="Open file"
                  >
                    <ExternalLink size={16} />
                  </a>
                  <button
                    onClick={() => deleteDocument(doc.id)}
                    className="icon-btn"
                    style={{ color: "var(--danger)" }}
                    title="Remove"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Step 4: Done */}
        </div>

        {/* Drawer Footer */}
        <div className="drawer-foot">
          <button
            onClick={step === 0 ? onClose : () => setStep((s) => s - 1)}
            className="btn btn-soft"
          >
            <ChevronLeft size={16} />
            {step === 0 ? "Cancel" : "Back"}
          </button>

          <div className="spacer" />

          <>
            {(step === 0 || step === 1) && (
              <button
                onClick={handleSaveOnly}
                disabled={saving}
                className="btn btn-soft"
                style={{ opacity: saving ? 0.5 : 1 }}
              >
                {saving ? (
                  <Loader2
                    size={16}
                    style={{ animation: "spin 1s linear infinite" }}
                  />
                ) : null}
                Save
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={saving}
              className="btn btn-primary"
              style={{ opacity: saving ? 0.5 : 1 }}
            >
              {saving ? (
                <Loader2
                  size={16}
                  style={{ animation: "spin 1s linear infinite" }}
                />
              ) : null}
              {step === 0 || step === 1
                ? "Save & Next"
                : step === 3
                  ? "Finish"
                  : "Next"}
              {!saving && <ChevronRight size={16} />}
            </button>
          </>
        </div>
      </div>
      <div className="scrim" style={{ display: "none" }} />
    </div>
  );
};

export default StudentForm;
